import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardSidebar } from "@/components/dashboard/DashboardSidebar";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { Footer } from "@/components/Footer";
import { ProfileStatusCard } from "@/components/dashboard/ProfileStatusCard";
import { PendingMatchesCard } from "@/components/dashboard/PendingMatchesCard";
import { ActiveMatchesCard } from "@/components/dashboard/ActiveMatchesCard";
import { QuickStatsCard } from "@/components/dashboard/QuickStatsCard";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Info } from "lucide-react";
import { useMissingRequiredFields } from "@/hooks/useMissingRequiredFields";
import { ProfileBackfillDialog } from "@/components/ProfileBackfillDialog";

interface ProfileData {
  profile_completed: boolean;
  is_active: boolean;
  created_at: string;
  first_name: string;
  profile_picture_url?: string;
}

interface MatchData {
  id: string;
  created_at: string;
  status: string;
  profile_1_id: string;
  profile_2_id: string;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [pendingMatches, setPendingMatches] = useState<MatchData[]>([]);
  const [activeMatchesCount, setActiveMatchesCount] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [acceptedMatches, setAcceptedMatches] = useState(0);
  const [backfillOpen, setBackfillOpen] = useState(false);

  // Manifest-driven backfill detection. Derived from actual data, not a version
  // counter — a future required field flows through here automatically once
  // added to src/lib/requiredFields.ts.
  const { missing: missingFields, refresh: refreshMissing } =
    useMissingRequiredFields(user?.id);

  const fetchDashboardData = async () => {
    if (!user) return;

    try {
      // Check if user is an admin
      const { data: adminRoleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (adminRoleData) {
        navigate("/admin/profiles");
        return;
      }

      // Check if user is a matchmaker
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "matchmaker")
        .maybeSingle();

      if (roleData) {
        navigate("/matchmaker/profiles");
        return;
      }

      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("profile_completed, is_active, created_at, first_name, profile_picture_url")
        .eq("id", user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profileData?.profile_completed) {
        navigate("/profile/create");
        return;
      }

      setProfile(profileData);

      // Fetch matches
      const { data: matchesData, error: matchesError } = await supabase
        .from("matches")
        .select("*")
        .or(`profile_1_id.eq.${user.id},profile_2_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (matchesError) throw matchesError;

      const matches = matchesData || [];
      setTotalMatches(matches.length);

      // Filter pending matches
      const pending = matches.filter(
        (m) => m.status !== "both_accepted" && m.status !== "rejected"
      );
      setPendingMatches(pending);

      // Count active matches
      const active = matches.filter((m) => m.status === "both_accepted");
      setActiveMatchesCount(active.length);

      // Count accepted matches (where user accepted)
      const userAccepted = matches.filter((m) => {
        if (m.profile_1_id === user.id && m.profile_1_response === "accepted") return true;
        if (m.profile_2_id === user.id && m.profile_2_response === "accepted") return true;
        return false;
      });
      setAcceptedMatches(userAccepted.length);
    } catch (error) {
      console.error("dashboard.fetch_failed");
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user, navigate]);

  // Real-time subscriptions
  useEffect(() => {
    if (!user) return;

    const matchesChannel = supabase
      .channel("matches-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "matches",
          filter: `profile_1_id=eq.${user.id},profile_2_id=eq.${user.id}`,
        },
        () => {
          fetchDashboardData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(matchesChannel);
    };
  }, [user]);

  const handleViewMatch = (matchId: string) => {
    navigate(`/dashboard/matches/${matchId}`);
  };

  const handleViewAllMatches = () => {
    navigate("/dashboard/matches");
  };

  if (loading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <DashboardSidebar />
        
        <div className="flex-1 flex flex-col">
          <DashboardHeader
            title="Dashboard"
            profilePicture={profile.profile_picture_url}
            firstName={profile.first_name}
          />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto bg-gradient-to-br from-primary/5 via-background to-secondary/5">
            <div className="container mx-auto p-6 max-w-7xl">
              {/* Backfill prompt — shown when the member is missing manifest-required
                  fields. Not a lockout; routes to a focused fill-in of just those. */}
              {missingFields.length > 0 && (
                <Alert className="mb-6 border-primary/30 bg-primary/5">
                  <Info className="h-4 w-4" />
                  <AlertTitle>Complete a few more details</AlertTitle>
                  <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <span>
                      We've added a few things to help our matchmakers make
                      introductions the traditional way:{" "}
                      {missingFields.map((f) => f.label.toLowerCase()).join(", ")}.
                    </span>
                    <Button
                      size="sm"
                      onClick={() => setBackfillOpen(true)}
                      className="shrink-0"
                    >
                      Add details
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {user && missingFields.length > 0 && (
                <ProfileBackfillDialog
                  open={backfillOpen}
                  missing={missingFields}
                  profileId={user.id}
                  onOpenChange={setBackfillOpen}
                  onCompleted={refreshMissing}
                />
              )}

              {/* Welcome Section */}
              <div className="mb-8 rounded-2xl bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 p-8 border border-primary/20 shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">👋</span>
                  <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Welcome back, {profile.first_name}!
                  </h1>
                </div>
                <p className="text-foreground/70 text-lg">
                  Your journey to finding your perfect match continues here. Let's make it happen! ✨
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">
                <ProfileStatusCard
                  profileCompleted={profile.profile_completed}
                  isActive={profile.is_active}
                  userId={user!.id}
                  onActiveToggle={(active) => setProfile({ ...profile, is_active: active })}
                />

                <QuickStatsCard
                  createdAt={profile.created_at}
                  totalMatches={totalMatches}
                  acceptedMatches={acceptedMatches}
                />

                <ActiveMatchesCard
                  count={activeMatchesCount}
                  onViewAll={handleViewAllMatches}
                />
              </div>

              {/* Pending Matches */}
              <PendingMatchesCard
                matches={pendingMatches}
                userId={user!.id}
                onViewMatch={handleViewMatch}
              />
            </div>
          </main>
          <Footer />
        </div>
      </div>
    </SidebarProvider>
  );
}
